import { ref, inject } from 'vue'
import { storeToRefs } from 'pinia'
import { useLanguageStore } from '@/stores/LanguageStore'
import { useClientStore } from '@/stores/ClientStore'
import { useBranding } from '@/vendors/branding'
import { useConsent } from '@/vendors/consent'
import { useSentry } from '@/vendors/integrations/sentry'
import { loadStripe as load } from '@stripe/stripe-js'
import { get as _get } from 'lodash'

const stripeSymbol = Symbol()

export const createStripe = {
  install: app => {
    const { getClient } = useClientStore()
    const { locale } = storeToRefs(useLanguageStore())
    const { hasMarketingConsent } = useConsent()
    const sentry = useSentry()

    const { client } = getClient()

    let stripe
    let elements

    const id = process.env.VUE_APP_STRIPE_ID
    const account_id = _get(client.value, 'integrations.stripe.account_id')

    const options = account_id ? { stripeAccount: account_id } : {}

    const is_stripe_processing = ref(false)

    const loadStripe = async () => {
      try {
        if (!id) throw new Error('errors.missing_stripe_id')

        if (!stripe) stripe = await load(id, options)

        return stripe
      } catch (e) {
        sentry.error(e)

        throw new Error('errors.failed_to_load_stripe')
      }
    }

    const initStipeElements = async () => {
      try {
        const { getFontFamily } = useBranding()

        await loadStripe()

        elements = stripe.elements({
          locale: locale.value,
          fonts: hasMarketingConsent.value ? [{ cssSrc: `https://fonts.googleapis.com/css?family=${getFontFamily().replace(/ /g, '+')}` }] : []
        })
      } catch (e) {
        sentry.error(e)

        throw new Error('errors.failed_to_initialize_stripe_elements')
      }
    }

    const createElement = (type, options = {}) => elements?.create(type, options)
    const getElement = type => elements?.getElement(type)
    const getElements = () => _get(elements, '_elements', [])
    const clearElements = () => getElements().forEach(element => element.clear())

    const createStripePaymentMethod = async params => {
      try {
        is_stripe_processing.value = true

        await loadStripe()
        const result = await stripe.createPaymentMethod(params)

        if (result.error) throw new Error()

        return result.paymentMethod.id
      } catch (e) {
        sentry.error(e)
        throw new Error('errors.an_error_occurred_while_processing_your_card_try_again_in_a_little_bit_transaction')
      } finally {
        is_stripe_processing.value = false
      }
    }

    app.provide(stripeSymbol, {
      is_stripe_processing,
      initStipeElements,
      createElement,
      getElement,
      clearElements,
      createStripePaymentMethod
    })
  }
}

export const useStripe = () => inject(stripeSymbol)

